export interface ResourceOrQuery {
  id: string;
  payload: any;
  type?: Resource;
}

export type Resource =
  | 'Logs'
  | 'Traces'
  | 'Errors'
  | 'Sessions'
  | 'Dashboard'
  | 'Graph'
  | 'Alert'
  | 'SavedSearch'
  | 'ExternalSource'
  | 'Contexts'
  | 'Flag';

export type ResourceType =
  | 'Sessions'
  | 'Logs'
  | 'Metrics'
  | 'Traces'
  | 'Errors'
  | 'Dashboard'
  | 'Graph'
  | 'Alert'
  | 'SavedSearch'
  | 'ExternalSource'
  | 'Flag'
  | 'Unknown';

export const EXTERNAL_SOURCE_TYPES = ['slack', 'confluence', 'jira', 'github', 'atlas'] as const;
export type ExternalSourceType = (typeof EXTERNAL_SOURCE_TYPES)[number];

// Runtime narrowing helper — payload.sourceType is typed `unknown` because it comes
// from open-shape resource payloads, so callers that need to set the typed hint must
// validate the value rather than `as`-casting an arbitrary string.
export const isExternalSourceType = (v: unknown): v is ExternalSourceType =>
  typeof v === 'string' && (EXTERNAL_SOURCE_TYPES as readonly string[]).includes(v);

/** Matches @[label](type:id) — the mention marker format embedded in message text */
export const MENTION_MARKER_REGEX = /@\[([^\]]+)\]\(([^:)]+):([^)]+)\)/;

export const inferContextType = (resource: ResourceOrQuery): ResourceType => {
  const payload = resource?.payload ?? {};
  const explicitType = resource?.type;

  if (explicitType) {
    return explicitType as ResourceType;
  }
  if (payload?.flag_key || payload?.flagKey) {
    return 'Flag';
  }
  if (payload?.secure_id) {
    return 'Sessions';
  }
  if (payload?.logAttributes || payload?.message || payload?.serviceName) {
    return 'Logs';
  }
  if (payload?.error_object_id) {
    return 'Errors';
  }
  if (payload?.traceID || payload?.trace_id || payload?.spanID || payload?.span_id) {
    return 'Traces';
  }
  if (payload?.metrics || payload?.aggregations || payload?.buckets) {
    return 'Metrics';
  }
  if (payload?.graph_id) {
    return 'Graph';
  }
  if (payload?.dashboard_id) {
    return 'Dashboard';
  }
  if (payload?.alert_id !== undefined) {
    return 'Alert';
  }
  if (payload?.saved_search_id || payload?.entity_type) {
    return 'SavedSearch';
  }
  if (payload?.url && payload?.sourceType) {
    return 'ExternalSource';
  }
  return 'Unknown';
};
